import * as React from "react";
import { FormLabel, GridItem, HStack, VStack } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { TextInput, AutoComplete } from "components";
import { GetPage, useOnline } from "duck";
import {
  GetStatesDocument,
  GetStatesQuery,
  GetStatesQueryVariables,
  GetCitiesDocument,
  GetCitiesQuery,
  GetCitiesQueryVariables,
  GetZipCodesDocument,
  GetZipCodesQuery,
  GetZipCodesQueryVariables
} from "requests";
import { Types as FormTypes } from "../../duck";
import { Types } from "./duck";

const Address: React.FC = () => {
  const online = useOnline();

  const { watch, setValue } = useFormContext<FormTypes.FormValues>();

  const stateId = watch("state");
  const cityId = watch("city");
  const zipCode = watch("zipCode");

  const getStatesPage: GetPage<Types.State> = React.useCallback(
    (client, variables) =>
      client
        .query<GetStatesQuery, GetStatesQueryVariables>({
          query: GetStatesDocument,
          variables
        })
        .then(({ data }) => ({
          collection: data.states?.collection,
          totalCount: data.states?.paginationInfo.totalCount
        })),
    []
  );

  const getCitiesPage: GetPage<Types.City> = React.useCallback(
    (client, variables) =>
      client
        .query<GetCitiesQuery, GetCitiesQueryVariables>({
          query: GetCitiesDocument,
          variables: {
            ...variables,
            ...(stateId ? { stateId } : {})
          }
        })
        .then(({ data }) => ({
          collection: data.cities?.collection,
          totalCount: data.cities?.paginationInfo?.totalCount
        })),
    [stateId]
  );

  const getZipCodePage: GetPage<Types.ZipCode> = React.useCallback(
    (client, variables) =>
      client
        .query<GetZipCodesQuery, GetZipCodesQueryVariables>({
          query: GetZipCodesDocument,
          variables: {
            ...variables,
            ...(stateId ? { stateId } : {}),
            ...(cityId ? { cityId } : {})
          }
        })
        .then(({ data }) => ({
          collection: data.zipCodes?.collection,
          totalCount: data.zipCodes?.paginationInfo.totalCount
        })),
    [stateId, cityId]
  );

  const getCityLabel = React.useCallback(
    (opt: Types.City) =>
      stateId ? opt.name : `${opt.name} (${opt.state.name})`,
    [stateId]
  );

  const zipSelected = zipCode !== null;

  return (
    <GridItem colSpan={2}>
      <FormLabel>Address</FormLabel>
      <VStack>
        <HStack width="100%" alignItems="flex-start">
          <TextInput
            name="streetAddress"
            label="Street Address"
            labelHidden
            placeholder="Street Address"
          />
          <TextInput
            name="apartments"
            label="Apt, suite, etc (optional)"
            labelHidden
            placeholder="Apt, suite, etc (optional)"
          />
        </HStack>

        <HStack width="100%" alignItems="flex-start">
          <AutoComplete
            isDisabled={!online}
            label="State"
            labelHidden
            name="state"
            placeholder="State"
            getPage={getStatesPage}
            isClearable
            onChange={value => {
              setValue("state", value, {
                shouldValidate: true,
                shouldDirty: true
              });
              if (value === null) {
                setValue("city", null, {
                  shouldValidate: true,
                  shouldDirty: cityId !== null,
                  shouldTouch: cityId !== null
                });
                setValue("zipCode", null, {
                  shouldValidate: true,
                  shouldDirty: zipSelected,
                  shouldTouch: zipSelected
                });
              }
            }}
          />
          <AutoComplete
            isDisabled={!online}
            label="City"
            labelHidden
            name="city"
            isClearable
            placeholder="City"
            getPage={getCitiesPage}
            optionLabel={getCityLabel}
            onChange={(value, opt) => {
              setValue("city", value, {
                shouldValidate: true,
                shouldDirty: true
              });
              setValue("zipCode", null, {
                shouldValidate: true,
                shouldDirty: zipSelected,
                shouldTouch: zipSelected
              });
              if (opt) {
                setValue("state", opt.state._id, { shouldValidate: true });
              }
            }}
          />
          <AutoComplete
            creatable
            optionValue="zipCode"
            isDisabled={!online}
            label="ZIP"
            labelHidden
            name="zipCode"
            placeholder="ZIP"
            isClearable
            getPage={getZipCodePage}
            optionLabel="zipCode"
            onChange={(value, opt) => {
              setValue("zipCode", value, {
                shouldValidate: true,
                shouldDirty: true
              });
              if (opt?.city) {
                setValue("city", opt.city._id, { shouldValidate: true });
                setValue("state", opt.city.state._id, { shouldValidate: true });
              }
            }}
          />
        </HStack>
      </VStack>
    </GridItem>
  );
};

export default Address;
